import React, { useEffect, useState } from "react";
import { Sparkles, Loader2, Dumbbell, Salad, MessageCircle, Copy, Check, Trash2, Lock, RefreshCcw } from "lucide-react";
import { endpoints } from "../lib/api";
import { isAIEnabled, planLockReason } from "../lib/plan";
import { openWhatsApp, waLink, markdownToWhatsApp } from "../lib/wa";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";

const KIND_OPTIONS = [
  { key: "both", label: "Workout + Diet", icon: Sparkles },
  { key: "workout", label: "Workout only", icon: Dumbbell },
  { key: "diet", label: "Diet only", icon: Salad },
];
const GOAL_OPTIONS = [
  { key: "weight_loss", label: "Weight loss" },
  { key: "muscle_gain", label: "Muscle gain" },
  { key: "general_fitness", label: "General fitness" },
  { key: "strength", label: "Strength" },
  { key: "endurance", label: "Endurance" },
  { key: "fight_prep", label: "Fight prep" },
];
const DISCIPLINE_GROUPS = [
  {
    label: "Gym / Fitness",
    options: [
      { key: "strength", label: "Strength training" },
      { key: "hypertrophy", label: "Hypertrophy / Bodybuilding" },
      { key: "functional", label: "Functional training" },
      { key: "calisthenics", label: "Calisthenics" },
      { key: "running", label: "Running" },
      { key: "general", label: "General fitness" },
    ],
  },
  {
    label: "Combat sport",
    options: [
      { key: "boxing", label: "Boxing 🥊" },
      { key: "kickboxing", label: "Kickboxing" },
      { key: "muay_thai", label: "Muay Thai" },
      { key: "mma", label: "MMA" },
      { key: "bjj", label: "Brazilian Jiu-Jitsu" },
    ],
  },
  {
    label: "Other",
    options: [
      { key: "custom", label: "Custom (use notes)" },
    ],
  },
];
const LEVEL_OPTIONS = [
  { key: "beginner", label: "Beginner" },
  { key: "intermediate", label: "Intermediate" },
  { key: "advanced", label: "Advanced" },
];
const DIET_OPTIONS = [
  { key: "veg", label: "Vegetarian" },
  { key: "non_veg", label: "Non-veg" },
  { key: "vegan", label: "Vegan" },
  { key: "jain", label: "Jain" },
  { key: "eggetarian", label: "Eggetarian" },
];
const LANG_OPTIONS = [
  { key: "en", label: "English" },
  { key: "hinglish", label: "Hinglish" },
  { key: "hi", label: "हिंदी" },
];

export default function AIPlanCard({ memberId, memberName, memberPhone }) {
  const aiEnabled = isAIEnabled();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    kind: "both", goal: "general_fitness", level: "beginner",
    days_per_week: 4, dietary_pref: "veg", discipline: "strength",
    lang: "en", notes: "",
  });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!memberId) return;
    if (!aiEnabled) { setLoading(false); return; }
    endpoints.getMemberPlan(memberId)
      .then(r => setPlan(r.data || null))
      .catch(() => setPlan(null))
      .finally(() => setLoading(false));
  }, [memberId, aiEnabled]);

  const generate = async (e) => {
    e?.preventDefault();
    if (!memberId || generating) return;
    setGenerating(true);
    try {
      const { data } = await endpoints.aiPlan({
        member_id: memberId,
        kind: form.kind,
        goal: form.goal,
        level: form.level,
        days_per_week: parseInt(form.days_per_week, 10) || 4,
        dietary_pref: form.dietary_pref,
        discipline: form.discipline,
        lang: form.lang,
        notes: form.notes || null,
      });
      setPlan(data);
      setOpen(false);
      toast.success("Plan generated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not generate plan");
    } finally { setGenerating(false); }
  };

  const remove = async () => {
    if (!plan || !window.confirm("Delete this saved plan?")) return;
    try {
      await endpoints.clearMemberPlan(memberId);
      setPlan(null);
      toast.success("Plan removed");
    } catch { toast.error("Could not delete"); }
  };

  const cleaned = plan?.markdown ? markdownToWhatsApp(plan.markdown) : "";

  const copyText = async () => {
    if (!cleaned) return;
    let ok = false;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(cleaned);
        ok = true;
      }
    } catch (_) {}
    if (!ok) {
      try {
        const ta = document.createElement("textarea");
        ta.value = cleaned;
        ta.style.position = "fixed"; ta.style.left = "-9999px";
        document.body.appendChild(ta); ta.focus(); ta.select();
        ok = document.execCommand("copy");
        document.body.removeChild(ta);
      } catch (_) {}
    }
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      toast.success("Copied to clipboard");
    } else {
      try { window.prompt("Copy this text:", cleaned); } catch (_) {}
    }
  };

  const downloadTxt = () => {
    if (!cleaned) return;
    try {
      const blob = new Blob([cleaned], { type: "text/plain;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = (memberName || "member").replace(/[^a-z0-9-]+/gi, "-").toLowerCase();
      a.download = `fitnudge-${safe}-plan.txt`;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        try { document.body.removeChild(a); } catch (_) {}
        URL.revokeObjectURL(url);
      }, 1000);
      toast.success("Plan downloaded");
    } catch (_) { toast.error("Download blocked - copy text instead"); }
  };

  const waSendHref = (memberPhone && cleaned)
    ? waLink(memberPhone, `Hey ${memberName ? memberName.split(" ")[0] : "there"}, here's your fresh plan from your trainer.\n\n${cleaned}`)
    : null;

  return (
    <div className="border border-border rounded-xl p-5 bg-card mb-6" data-testid="ai-plan-card">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2.5">
          <span
            className="inline-flex items-center justify-center w-9 h-9 rounded-lg text-white shrink-0"
            style={{ background: "linear-gradient(135deg, #7C3AED 0%, #DB2777 50%, #F97316 100%)" }}
            aria-hidden
          >
            <Dumbbell className="w-4 h-4" />
          </span>
          <div>
            <h3 className="font-display text-base font-semibold text-foreground leading-none flex items-center gap-2">
              AI Workout & Diet
              <span className="text-[9px] font-bold uppercase tracking-wider bg-foreground text-background px-1.5 py-0.5 rounded">beta</span>
            </h3>
            <p className="text-[11px] text-muted-foreground mt-1">
              Personalised for {memberName || "this member"} · share via WhatsApp
            </p>
          </div>
        </div>
        {aiEnabled && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            disabled={generating}
            data-testid="ai-plan-generate-btn"
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg text-white transition-opacity disabled:opacity-60"
            style={{ background: "linear-gradient(135deg, #7C3AED 0%, #DB2777 60%, #F97316 100%)" }}
          >
            {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : plan ? <RefreshCcw className="w-3.5 h-3.5" /> : <Sparkles className="w-3.5 h-3.5" />}
            {plan ? "New plan" : "Generate plan"}
          </button>
        )}
      </div>

      {!aiEnabled ? (
        <div data-testid="ai-plan-locked" className="rounded-lg border border-dashed border-purple-300/60 dark:border-purple-700/40 p-4 text-center">
          <Lock className="w-5 h-5 mx-auto mb-2 text-purple-500" />
          <p className="text-sm font-medium text-foreground mb-1">AI workout & diet plans</p>
          <p className="text-xs text-muted-foreground max-w-md mx-auto">{planLockReason()}</p>
        </div>
      ) : loading ? (
        <div className="h-20 bg-muted/40 rounded-lg animate-pulse" />
      ) : !plan ? (
        <div className="text-center py-6 text-sm text-muted-foreground/80" data-testid="ai-plan-empty">
          No plan yet. Click <strong className="text-foreground">Generate plan</strong> to create a personalised workout + diet for {memberName?.split(" ")[0] || "this member"}.
        </div>
      ) : (
        <div className="space-y-3" data-testid="ai-plan-content">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <p className="font-display text-sm font-semibold text-foreground">{plan.summary}</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                {plan.discipline?.replace(/_/g, " ") || ""}{plan.discipline ? " · " : ""}{plan.goal?.replace(/_/g, " ")} · {plan.level} · {plan.days_per_week}d/wk · {plan.dietary_pref?.replace(/_/g, "-")} · {new Date(plan.created_at).toLocaleDateString()}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button type="button" onClick={copyText} data-testid="ai-plan-copy-btn" className="inline-flex items-center gap-1 text-xs bg-card border border-border hover:bg-muted/50 text-foreground px-2.5 py-1.5 rounded-md transition-colors">
                {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />} {copied ? "Copied" : "Copy"}
              </button>
              <button type="button" onClick={downloadTxt} data-testid="ai-plan-download-btn" className="inline-flex items-center gap-1 text-xs bg-card border border-border hover:bg-muted/50 text-foreground px-2.5 py-1.5 rounded-md transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                Download
              </button>
              {waSendHref ? (
                <a
                  href={waSendHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="ai-plan-send-wa"
                  className="inline-flex items-center gap-1 text-xs bg-[#25D366] hover:bg-[#1EBE5D] text-white font-semibold px-2.5 py-1.5 rounded-md transition-colors"
                >
                  <MessageCircle className="w-3.5 h-3.5" /> Send
                </a>
              ) : (
                <button type="button" disabled data-testid="ai-plan-send-wa-disabled" className="inline-flex items-center gap-1 text-xs bg-[#25D366]/40 text-white font-semibold px-2.5 py-1.5 rounded-md cursor-not-allowed">
                  <MessageCircle className="w-3.5 h-3.5" /> Send
                </button>
              )}
              <button type="button" onClick={remove} aria-label="Delete plan" data-testid="ai-plan-delete-btn" className="p-1.5 text-muted-foreground/70 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30 rounded-md">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          <div
            data-testid="ai-plan-markdown"
            className="border border-border rounded-lg p-4 bg-background/40 max-h-[420px] overflow-y-auto"
          >
            <PlanMarkdown text={plan.markdown} />
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Generate AI plan</DialogTitle></DialogHeader>
          <form onSubmit={generate} className="space-y-4" data-testid="ai-plan-form">
            <div className="grid grid-cols-2 gap-3">
              <FormSelect label="Plan type" value={form.kind} onChange={v => setForm({ ...form, kind: v })} options={KIND_OPTIONS} testid="plan-kind" />
              <FormSelect label="Goal" value={form.goal} onChange={v => setForm({ ...form, goal: v })} options={GOAL_OPTIONS} testid="plan-goal" />
            </div>
            <div>
              <Label>Discipline / sport</Label>
              <Select value={form.discipline} onValueChange={v => setForm({ ...form, discipline: v })}>
                <SelectTrigger data-testid="plan-discipline"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DISCIPLINE_GROUPS.map(group => (
                    <React.Fragment key={group.label}>
                      <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70 mt-1 first:mt-0">
                        {group.label}
                      </div>
                      {group.options.map(o => (
                        <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>
                      ))}
                    </React.Fragment>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <FormSelect label="Level" value={form.level} onChange={v => setForm({ ...form, level: v })} options={LEVEL_OPTIONS} testid="plan-level" />
              <FormSelect label="Diet" value={form.dietary_pref} onChange={v => setForm({ ...form, dietary_pref: v })} options={DIET_OPTIONS} testid="plan-diet" />
              <div>
                <Label>Days / week</Label>
                <Input type="number" min={2} max={7} value={form.days_per_week} onChange={e => setForm({ ...form, days_per_week: e.target.value })} data-testid="plan-days" />
              </div>
            </div>
            <FormSelect label="Language" value={form.lang} onChange={v => setForm({ ...form, lang: v })} options={LANG_OPTIONS} testid="plan-lang" />
            <div>
              <Label>Notes (optional)</Label>
              <Input
                placeholder="e.g. knee injury, vegetarian only on weekdays"
                value={form.notes}
                onChange={e => setForm({ ...form, notes: e.target.value })}
                data-testid="plan-notes"
              />
            </div>
            <DialogFooter>
              <button
                type="submit"
                disabled={generating}
                data-testid="plan-submit"
                className="w-full text-white text-sm font-semibold py-2.5 rounded-lg disabled:opacity-60 transition-opacity"
                style={{ background: "linear-gradient(135deg, #7C3AED 0%, #DB2777 60%, #F97316 100%)" }}
              >
                {generating ? "Generating…" : "Generate plan"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FormSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>
          {options.map(o => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

// Tiny markdown renderer - handles ##, ###, lists, **bold**, em-dashes, line breaks.
function PlanMarkdown({ text }) {
  if (!text) return null;
  const lines = text.split("\n");
  const blocks = [];
  let listBuf = null;

  const flushList = () => {
    if (listBuf) {
      blocks.push(
        <ul key={`ul-${blocks.length}`} className="list-disc pl-5 space-y-1 text-sm text-foreground/85">
          {listBuf.map((it, i) => <li key={i}>{inlineRender(it)}</li>)}
        </ul>
      );
      listBuf = null;
    }
  };

  lines.forEach((rawLine, idx) => {
    const line = rawLine.trimEnd();
    if (line.startsWith("### ")) {
      flushList();
      blocks.push(<h4 key={idx} className="font-display text-sm font-semibold text-foreground mt-3 mb-1">{inlineRender(line.slice(4))}</h4>);
    } else if (line.startsWith("## ")) {
      flushList();
      blocks.push(<h3 key={idx} className="font-display text-base font-bold text-foreground mt-4 mb-2">{inlineRender(line.slice(3))}</h3>);
    } else if (line.startsWith("# ")) {
      flushList();
      blocks.push(<h2 key={idx} className="font-display text-lg font-bold text-foreground mt-3 mb-2">{inlineRender(line.slice(2))}</h2>);
    } else if (/^\s*[-*]\s+/.test(line)) {
      const item = line.replace(/^\s*[-*]\s+/, "");
      listBuf = listBuf || [];
      listBuf.push(item);
    } else if (/^\s*\d+\.\s+/.test(line)) {
      const item = line.replace(/^\s*\d+\.\s+/, "");
      listBuf = listBuf || [];
      listBuf.push(item);
    } else if (line.trim() === "" || line.trim() === "---") {
      flushList();
      blocks.push(<div key={idx} className="h-2" />);
    } else {
      flushList();
      blocks.push(<p key={idx} className="text-sm text-foreground/85 leading-relaxed">{inlineRender(line)}</p>);
    }
  });
  flushList();
  return <div className="space-y-1">{blocks}</div>;
}

function inlineRender(text) {
  // **bold** support
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) {
      return <strong key={i} className="font-semibold text-foreground">{p.slice(2, -2)}</strong>;
    }
    return p;
  });
}
